import React, { useEffect, useState } from "react";
import CardLayout from "components/foundation/templates/CardLayout";
import SelectButton from "components/foundation/SelectButton";
import { CheckIcon } from "assets/icons";
import Button from "components/foundation/Button";
import Divider from "components/foundation/Divider";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { DOCUMENT_UUID } from "config/constant";
import useTagsByDocumentCategoryQuery from "features/tag/hooks/queries/useTagsByDocumentCategoryQuery";
import FreeTagEditor from "../Create/CreateDocumentForm/CreateDocumentFormHeader/FreeTagEditor";
import { FreeTagDetailDto, TagDetailDto } from "apis/data-contracts";
import { documentFreeTagsState } from "store/document/documentCommon";
import useWorkSelectFormStates from "features/document/hooks/create/useWorkSelectFormStates";
import { useUpdateDocumentTags } from "features/document/hooks/mutations/useUpdateDocumentTags";
import Modal from "components/foundation/Modal/Modal";
import useUpdateDocumentFileUrl from "features/pdf/mutations/useUpdateDocumentFileUrl";

interface WorkTagEditSelectProps {
  freeTags?: FreeTagDetailDto[];
  tags?: TagDetailDto[];
  onClose: (isSuccess?: boolean) => void;
  uuid: string;
  onTagUpdateSuccess: () => void;
  uploadDocPdfAndGetS3Url?: (fileName: string) => Promise<string>;
}

const WorkTagEditSelect = ({
  freeTags,
  tags,
  onClose,
  uuid,
  onTagUpdateSuccess,
  uploadDocPdfAndGetS3Url,
}: WorkTagEditSelectProps) => {
  const { data: workTags } = useTagsByDocumentCategoryQuery(
    DOCUMENT_UUID.WORK.uuid,
  );

  const pjOptions = workTags?.tagCategories[0].tags;
  const topicOptions = workTags?.tagCategories[1].tags;

  const { selectedPj, setSelectedPj, selectedTopics, setSelectedTopics } =
    useWorkSelectFormStates();

  const [selectedFreeTags, setSelectedFreeTags] = useRecoilState(
    documentFreeTagsState,
  );

  // 자유 태그
  useEffect(() => {
    if (freeTags && freeTags.length === 0) {
      setSelectedFreeTags([]);
    } else if (freeTags && freeTags.length > 0) {
      setSelectedFreeTags(freeTags);
    }
  }, [freeTags, setSelectedFreeTags]);

  // PJ 태그
  useEffect(() => {
    if (tags && tags.length === 0) {
      setSelectedPj(undefined);
    } else if (tags && pjOptions && pjOptions.length > 0) {
      const selectedPj = pjOptions.find((pjTag) =>
        tags.some((tag) => tag.tagUuid === pjTag.tagUuid),
      );
      if (selectedPj) {
        setSelectedPj(selectedPj);
      }
    }
  }, [tags, pjOptions, setSelectedPj]);

  // 주제 태그
  useEffect(() => {
    if (tags && tags.length === 0) {
      setSelectedTopics([]);
    } else if (tags && topicOptions && topicOptions.length > 0) {
      const selectedTopics = topicOptions.filter((topicTag) =>
        tags.some((tag) => tag.tagUuid === topicTag.tagUuid),
      );
      if (selectedTopics.length > 0) {
        setSelectedTopics(selectedTopics);
      }
    }
  }, [tags, topicOptions, setSelectedTopics]);

  /* 완료 버튼 */
  const [isOpenModal, setIsOpenModal] = useState(false);
  const { mutate: updateDocumentTags } = useUpdateDocumentTags();
  const { mutate: updateDocumentFileUrl } = useUpdateDocumentFileUrl();

  const onClickConfirm = () => {
    setIsOpenModal(true);
  };

  const handleUpdateTags = async () => {
    updateDocumentTags(
      {
        uuid: uuid,
        data: {
          tagUuids: [
            ...(selectedPj ? [selectedPj.tagUuid] : []),
            ...(selectedTopics
              ? selectedTopics.map((topic) => topic.tagUuid)
              : []),
          ],
          freeTagUuids: selectedFreeTags.map((tag) => tag.uuid),
        },
      },
      {
        onSuccess: async () => {
          onClose(true);

          // PDF 업데이트
          if (uploadDocPdfAndGetS3Url) {
            try {
              const s3Url = await uploadDocPdfAndGetS3Url("octo.pdf");
              updateDocumentFileUrl(
                {
                  uuid: uuid,
                  data: {
                    documentFileUrl: s3Url,
                  },
                },
                {
                  onSuccess: () => {
                    console.log("PDF URL 업데이트 완료");
                  },
                },
              );
            } catch (error) {
              console.error("PDF 업데이트 실패:", error);
            }
          }

          // 성공 콜백 실행
          await onTagUpdateSuccess();
        },
      },
    );
  };

  return (
    <>
      <CardLayout.Container
        header={{
          title: "태그 수정",
          description:
            "문서에 들어갈 기본 태그를 수정 또는 추가할 수 있습니다.",
        }}
        gap={40}
        headerColor="#171D23"
      >
        <DocumentAnalysisCreateForm
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <CardLayout.Field label="자유태그">
            <FreeTagEditor isTitle={false} />
          </CardLayout.Field>
          <CardLayout.Field label="PJ명">
            <SelectContainerStyle>
              {pjOptions?.map((pj) => {
                const selected = pj.tagUuid === selectedPj?.tagUuid;
                return (
                  <SelectButton
                    key={pj.tagUuid}
                    type="button"
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => setSelectedPj(pj)}
                  >
                    {pj.tagName}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
          <CardLayout.Field label="주제 분류">
            <SelectContainerStyle>
              {topicOptions?.map((topic) => {
                const selected = !!selectedTopics.find(
                  (item) => item.tagUuid === topic.tagUuid,
                );
                return (
                  <SelectButton
                    key={topic.tagUuid}
                    type="button"
                    value={topic.tagName}
                    selected={selected}
                    rightIcon={selected && <CheckIcon />}
                    onClick={() => {
                      const isSelected = selectedTopics.some(
                        (item) => item.tagUuid === topic.tagUuid,
                      );
                      if (isSelected) {
                        setSelectedTopics(
                          selectedTopics.filter(
                            (item) => item.tagUuid !== topic.tagUuid,
                          ),
                        );
                      } else {
                        setSelectedTopics([...selectedTopics, topic]);
                      }
                    }}
                  >
                    {topic.tagName}
                  </SelectButton>
                );
              })}
            </SelectContainerStyle>
          </CardLayout.Field>
        </DocumentAnalysisCreateForm>
        <Divider />
        <ButtonWrapperStyle>
          <Button
            variant="tertiary"
            size="large"
            fill
            onClick={() => onClose(false)}
          >
            취소
          </Button>
          <Button variant="primary" size="large" fill onClick={onClickConfirm}>
            완료
          </Button>
        </ButtonWrapperStyle>
      </CardLayout.Container>
      <Modal.Root open={isOpenModal} onOpenChange={setIsOpenModal}>
        <Modal.Body
          onConfirm={handleUpdateTags}
          onCancel={() => setIsOpenModal(false)}
          title="태그를 수정하시겠습니까?"
        ></Modal.Body>
      </Modal.Root>
    </>
  );
};

export default WorkTagEditSelect;

/* Styles */
const DocumentAnalysisCreateForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 5rem;
`;

const SelectContainerStyle = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(14.2rem, 1fr));
  gap: 1rem;
`;

const ButtonWrapperStyle = styled.div`
  display: flex;
  gap: 1rem;
`;
